import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';
import type { UploadedFile } from '~/types/models';

export function useUploadedFileSelection(files: MaybeRefOrGetter<UploadedFile[]>) {
    const selectedFileIds = ref<string[]>([]);
    const selectedFiles = computed(() => {
        const selectedIds = new Set(selectedFileIds.value);
        return toValue(files).filter((file) => selectedIds.has(file.id));
    });

    function toggleFileSelection(fileId: string) {
        selectedFileIds.value = selectedFileIds.value.includes(fileId)
            ? selectedFileIds.value.filter((id) => id !== fileId)
            : [...selectedFileIds.value, fileId];
    }

    function clearFileSelection() {
        selectedFileIds.value = [];
    }

    function selectionStateFor(fileIds: string[]): boolean | 'indeterminate' {
        const ids = [...new Set(fileIds)].filter(Boolean);
        if (ids.length === 0) return false;

        const selected = new Set(selectedFileIds.value);
        const selectedCount = ids.filter((id) => selected.has(id)).length;
        if (selectedCount === 0) return false;
        if (selectedCount === ids.length) return true;
        return 'indeterminate';
    }

    function toggleAllFileSelection(fileIds: string[]) {
        const ids = [...new Set(fileIds)].filter(Boolean);
        if (ids.length === 0) return;

        const selected = new Set(selectedFileIds.value);
        const allSelected = ids.every((id) => selected.has(id));

        if (allSelected) {
            const idsToClear = new Set(ids);
            selectedFileIds.value = selectedFileIds.value.filter((id) => !idsToClear.has(id));
            return;
        }

        ids.forEach((id) => selected.add(id));
        selectedFileIds.value = [...selected];
    }

    watch(() => toValue(files).map((file) => file.id), (availableIds) => {
        const available = new Set(availableIds);
        selectedFileIds.value = selectedFileIds.value.filter((id) => available.has(id));
    });

    return {
        selectedFileIds,
        selectedFiles,
        toggleFileSelection,
        toggleAllFileSelection,
        selectionStateFor,
        clearFileSelection,
    };
}
